import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Loader2, Wallet, QrCode, Banknote, Copy, ExternalLink, ShieldAlert, Receipt } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;
const METHODS = [
  { id: "ewallet", label: "E-WALLET", icon: Wallet, hint: "GCash / Maya / GrabPay" },
  { id: "qrph", label: "QRPH", icon: QrCode, hint: "Scan with any banking app" },
  { id: "vca", label: "BANK / VCA", icon: Banknote, hint: "InstaPay / PESONet transfer" },
];

const CheckoutPage = () => {
  const { orderId } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [method, setMethod] = useState("ewallet");
  const [useMock, setUseMock] = useState(true);
  const [banks, setBanks] = useState([]);
  const [bankCode, setBankCode] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState(null);
  const [polling, setPolling] = useState(false);

  // Receipt Details
  const [savedRDs, setSavedRDs] = useState([]);
  const [rdMode, setRdMode] = useState("own"); // own | saved | new
  const [selectedRdId, setSelectedRdId] = useState("");
  const [rd, setRd] = useState({ customer_name: "", address: "", tin: "" });
  const [saveRd, setSaveRd] = useState(false);

  useEffect(() => {
    api.get(`/orders/${orderId}`).then(({ data }) => setOrder(data)).catch(() => toast.error("Order not found"));
  }, [orderId]);

  // Load saved receipt details if logged in
  useEffect(() => {
    if (!user) return;
    api.get("/me/receipt-details").then(({ data }) => {
      setSavedRDs(data || []);
    }).catch(() => {});
  }, [user]);

  // Sync RD form based on mode/selection
  useEffect(() => {
    if (rdMode === "own" && user) {
      setRd({ customer_name: user.name || "", address: user.address || "", tin: user.tin || "" });
    } else if (rdMode === "saved" && selectedRdId) {
      const r = savedRDs.find((x) => x.rd_id === selectedRdId);
      if (r) setRd({ customer_name: r.customer_name, address: r.address, tin: r.tin || "" });
    } else if (rdMode === "new") {
      setRd({ customer_name: "", address: "", tin: "" });
    }
  }, [rdMode, selectedRdId, savedRDs, user]);

  // Load banks when e-wallet picked
  useEffect(() => {
    if (method !== "ewallet") return;
    api.get("/payments/aio/banks?type=ewallet").then(({ data }) => {
      setBanks(data);
      if (data?.length && !bankCode) setBankCode(data[0].code);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method]);

  // Poll order status when result is showing (live mode)
  useEffect(() => {
    if (!result || useMock) return;
    setPolling(true);
    const t = setInterval(() => {
      api.get(`/orders/${orderId}`).then(({ data }) => {
        if (data.status !== "pending_payment") {
          setOrder(data); clearInterval(t); setPolling(false);
          toast.success("Payment confirmed!");
        }
      }).catch(() => {});
    }, 4000);
    return () => { clearInterval(t); setPolling(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, useMock]);

  const initiatePayment = async () => {
    if (method === "ewallet" && (!bankCode || !accountNo)) {
      toast.error("Pick a wallet and enter your account/mobile number"); return;
    }
    // Persist receipt details to the order first (logged-in users with chosen RD)
    if (user && rd.customer_name && rd.address) {
      try {
        await api.patch(`/orders/${orderId}/receipt-details`, rd);
        if (rdMode === "new" && saveRd) {
          await api.post("/me/receipt-details", rd);
          toast.success("Receipt details saved to your profile");
          // refresh saved list silently
          api.get("/me/receipt-details").then(({ data }) => setSavedRDs(data || [])).catch(() => {});
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to save receipt details");
        return;
      }
    }
    setPaying(true);
    try {
      const { data } = await api.post("/payments/aio/initiate", {
        order_id: orderId, method, use_mock: useMock,
        bank_code: method === "ewallet" ? bankCode : null,
        account_no: method === "ewallet" ? accountNo : null,
      });
      setResult(data);
      // For mock e-wallet: simulate the success since gateway won't call our webhook
      if (useMock) {
        await api.post("/payments/confirm", { order_id: orderId, method });
        const updated = await api.get(`/orders/${orderId}`);
        setOrder(updated.data);
        toast.success("Payment confirmed (MOCK)");
      } else if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        toast.success("Payment instructions ready — complete payment on your device");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment initiation failed");
    } finally { setPaying(false); }
  };

  if (!order) return <div className="p-12 text-center text-neutral-500">Loading order…</div>;
  const isPaid = order.status !== "pending_payment";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pav-fade-in" data-testid="checkout-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Checkout</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0B1E3F] mt-2">Order {order.order_id}</h1>
      </div>

      <div className="pav-card mb-6">
        <h2 className="font-display text-lg font-bold mb-3">Summary</h2>
        {order.items.map((it, idx) => (
          <div key={idx} className="flex justify-between text-sm py-1.5 border-b border-[#E2E8F0] last:border-0">
            <span>{it.width}×{it.height} {it.unit} • {(it.finishing || "").replace("_", " ")} • × {it.quantity}</span>
            <span>{peso(it.line_total)}</span>
          </div>
        ))}
        <div className="flex justify-between text-sm mt-3"><span>Subtotal</span><span>{peso(order.subtotal)}</span></div>
        <div className="flex justify-between text-sm"><span>Shipping</span><span>{peso(order.shipping_cost)}</span></div>
        <div className="flex justify-between items-baseline pt-3 mt-3 border-t border-[#E2E8F0]">
          <span className="uppercase tracking-wider font-semibold text-sm">Total</span>
          <span className="font-display font-extrabold text-3xl text-[#0B1E3F]" data-testid="checkout-total">{peso(order.total)}</span>
        </div>
      </div>

      {isPaid ? (
        <div className="pav-card bg-green-50 border-green-200" data-testid="payment-success-card">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-green-700" />
            <div>
              <h2 className="font-display text-xl font-bold text-green-800">Payment confirmed</h2>
              <p className="text-sm text-green-700">We&apos;ll start production shortly. Track your order anytime.</p>
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <Link to={`/track?id=${order.order_id}`} className="btn-primary" data-testid="checkout-track-link">Track order</Link>
            <Link to="/dashboard" className="btn-outline" data-testid="checkout-dashboard-link">Go to dashboard</Link>
          </div>
        </div>
      ) : (
        <>
        {/* Receipt details panel — logged-in only */}
        {user && (
          <div className="pav-card mb-6" data-testid="receipt-details-panel">
            <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
              <Receipt className="h-5 w-5" /> Receipt customer details
            </h2>
            <p className="text-xs text-neutral-500 mb-3">Choose how the official receipt should be addressed.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              <label className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer ${rdMode === "own" ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0]"}`}>
                <input type="radio" name="rd_mode" value="own" checked={rdMode === "own"}
                  data-testid="rd-mode-own"
                  onChange={() => setRdMode("own")} />
                <div>
                  <p className="font-semibold text-sm">My account info</p>
                  <p className="text-[11px] text-neutral-500 mt-0.5">Use the name/address on file</p>
                </div>
              </label>
              <label className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer ${rdMode === "saved" ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0]"} ${savedRDs.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}>
                <input type="radio" name="rd_mode" value="saved" checked={rdMode === "saved"}
                  data-testid="rd-mode-saved"
                  disabled={savedRDs.length === 0}
                  onChange={() => { setRdMode("saved"); if (!selectedRdId && savedRDs[0]) setSelectedRdId(savedRDs[0].rd_id); }} />
                <div>
                  <p className="font-semibold text-sm">Saved receipt</p>
                  <p className="text-[11px] text-neutral-500 mt-0.5">{savedRDs.length === 0 ? "None saved yet" : `${savedRDs.length} saved`}</p>
                </div>
              </label>
              <label className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer ${rdMode === "new" ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0]"}`}>
                <input type="radio" name="rd_mode" value="new" checked={rdMode === "new"}
                  data-testid="rd-mode-new"
                  onChange={() => setRdMode("new")} />
                <div>
                  <p className="font-semibold text-sm">Use new details</p>
                  <p className="text-[11px] text-neutral-500 mt-0.5">Enter once-off info</p>
                </div>
              </label>
            </div>

            {rdMode === "saved" && savedRDs.length > 0 && (
              <select className="pav-input mb-3" value={selectedRdId} onChange={(e) => setSelectedRdId(e.target.value)}
                data-testid="rd-saved-select">
                <option value="">Choose a saved receipt</option>
                {savedRDs.map((r) => (
                  <option key={r.rd_id} value={r.rd_id}>
                    {r.customer_name} — {r.address}{r.tin ? ` · TIN ${r.tin}` : ""}
                  </option>
                ))}
              </select>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input className="pav-input" placeholder="Customer name" data-testid="rd-name-input"
                value={rd.customer_name}
                onChange={(e) => setRd({ ...rd, customer_name: e.target.value })}
                readOnly={rdMode !== "new"} />
              <input className="pav-input" placeholder="TIN (optional)" data-testid="rd-tin-input"
                value={rd.tin || ""}
                onChange={(e) => setRd({ ...rd, tin: e.target.value })}
                readOnly={rdMode !== "new"} />
              <input className="pav-input sm:col-span-2" placeholder="Address" data-testid="rd-address-input"
                value={rd.address}
                onChange={(e) => setRd({ ...rd, address: e.target.value })}
                readOnly={rdMode !== "new"} />
            </div>

            {rdMode === "new" && (
              <label className="mt-3 inline-flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={saveRd} onChange={(e) => setSaveRd(e.target.checked)}
                  data-testid="rd-save-checkbox" />
                Save these details to my profile for next time
              </label>
            )}
          </div>
        )}

        <div className="pav-card">
          <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
            <CreditCard className="h-5 w-5" /> Payment method
          </h2>

          {/* Mock toggle */}
          <label className={`flex items-center gap-2 mb-4 p-3 rounded-xl cursor-pointer ${useMock ? "bg-amber-50 border border-amber-200" : "bg-emerald-50 border border-emerald-200"}`}>
            <input type="checkbox" checked={useMock} onChange={(e) => setUseMock(e.target.checked)}
              data-testid="mock-payment-toggle" />
            <ShieldAlert className={`h-4 w-4 ${useMock ? "text-amber-600" : "text-emerald-700"}`} />
            <div className="text-xs">
              <p className="font-semibold">{useMock ? "Mock payment (no real charge)" : "LIVE — real charge via AIO Payment Gateway"}</p>
              <p className="text-neutral-500 mt-0.5">{useMock ? "Uncheck to charge the customer via AIO production API." : "✅ Production gateway will be contacted on submit."}</p>
            </div>
          </label>

          {/* Method tabs */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            {METHODS.map((m) => (
              <button key={m.id} type="button" onClick={() => { setMethod(m.id); setResult(null); }}
                data-testid={`method-${m.id}-button`}
                className={`p-3 rounded-xl border-2 text-left transition-all ${method === m.id ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0] hover:border-[#0B1E3F]/50"}`}>
                <m.icon className="h-5 w-5 mb-1.5 text-[#0B1E3F]" />
                <p className="text-sm font-semibold">{m.label}</p>
                <p className="text-[10px] text-neutral-500 mt-0.5">{m.hint}</p>
              </button>
            ))}
          </div>

          {/* E-wallet fields */}
          {method === "ewallet" && !result && (
            <div className="space-y-3 mb-4" data-testid="ewallet-form">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Wallet</label>
                <select className="pav-input mt-1" value={bankCode} onChange={(e) => setBankCode(e.target.value)} data-testid="ewallet-bank-select">
                  {banks.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Account / Mobile number</label>
                <input className="pav-input mt-1" value={accountNo} onChange={(e) => setAccountNo(e.target.value)}
                  placeholder="09171234567" data-testid="ewallet-account-input" />
              </div>
            </div>
          )}

          {/* Result display */}
          {result && method === "vca" && (
            <div className="rounded-xl bg-[#F4F6FB] border border-[#E2E8F0] p-5 mb-4 text-center" data-testid="vca-result">
              <p className="text-xs uppercase tracking-wider font-semibold text-neutral-500">Transfer to this virtual account</p>
              <p className="font-mono text-2xl font-bold text-[#0B1E3F] mt-2 tracking-wider">{result.vca}</p>
              <button onClick={() => { navigator.clipboard.writeText(result.vca); toast.success("Copied!"); }}
                className="text-xs text-[#06B6D4] mt-1 inline-flex items-center gap-1 hover:underline">
                <Copy className="h-3 w-3" /> Copy
              </button>
              <p className="text-sm text-neutral-600 mt-4">Amount: <strong>{peso(result.amountToPay || order.total)}</strong></p>
              <p className="text-xs text-neutral-500 mt-3">Send via InstaPay/PESONet from any bank. We&apos;ll confirm automatically.</p>
              {polling && <p className="text-xs text-[#06B6D4] mt-2 inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Waiting for payment…</p>}
            </div>
          )}
          {result && method === "qrph" && (
            <div className="rounded-xl bg-white border border-[#E2E8F0] p-5 mb-4 text-center" data-testid="qrph-result">
              <p className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-3">Scan with any banking app</p>
              <div className="inline-block p-3 bg-white rounded-xl">
                <QRCodeSVG value={result.qrCode} size={220} level="M" includeMargin={false} />
              </div>
              <p className="text-sm text-neutral-600 mt-4">Amount: <strong>{peso(order.total)}</strong></p>
              {polling && <p className="text-xs text-[#06B6D4] mt-2 inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Waiting for payment…</p>}
            </div>
          )}
          {result && method === "ewallet" && result.redirectUrl && !useMock && (
            <a href={result.redirectUrl} className="btn-primary w-full inline-flex items-center justify-center gap-2 mb-4" data-testid="ewallet-redirect-button">
              <ExternalLink className="h-4 w-4" /> Open {bankCode} to pay
            </a>
          )}

          <button onClick={initiatePayment} disabled={paying}
            className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="confirm-payment-button">
            {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {result ? "Re-initiate payment" : `Pay ${peso(order.total)}`}
          </button>
          <p className="text-[10px] text-neutral-400 text-center mt-3">Powered by AIO Payment Gateway · PCI-DSS compliant</p>
        </div>
        </>
      )}
    </div>
  );
};

export default CheckoutPage;
